/**
 * EF-TRAVEL-ADVISOR-01: Travel Advisor Modal
 * 
 * NON-DESTRUCTIVE advisory tool that provides travel estimates.
 * 
 * IMPORTANT:
 * - This is ADVISORY ONLY
 * - Does NOT auto-write costs
 * - Does NOT mutate financial totals  
 * - Does NOT create line items automatically
 * - Only suggests values for user review
 * 
 * UI REFINEMENTS:
 * - FROM ADDRESS: Dropdown of ACTIVE staff with valid addresses
 * - DESTINATION ADDRESS: Pre-filled from venue, editable
 */

import { useState, useEffect, useMemo } from "react";
import { MapPin, Clock, Car, AlertTriangle, Calculator, CheckCircle, Loader2, Info, User } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { getTravelAdvisory } from "../../lib/api";

/**
 * Helper to build full address string from staff address object
 */
const buildAddressString = (address) => {
  if (!address) return "";
  const parts = [
    address.line1,
    address.line2,
    address.city,
    address.postcode,
    address.country
  ].filter(Boolean);
  return parts.join(", ");
};

/**
 * Check if staff has a valid address for travel calculation
 */
const hasValidAddress = (staff) => {
  // Check for structured address object
  if (staff.address) {
    return !!(staff.address.line1 || staff.address.city || staff.address.postcode);
  }
  // Check for flat address fields (from CSV import)
  return !!(staff.address_line_1 || staff.address_city);
};

/**
 * Get display address for staff
 */
const getStaffDisplayAddress = (staff) => {
  if (staff.address) {
    return buildAddressString(staff.address);
  }
  // Build from flat fields
  const parts = [
    staff.address_line_1,
    staff.address_city,
    staff.address_country
  ].filter(Boolean);
  return parts.join(", ");
};

export function TravelAdvisorModal({
  open,
  onOpenChange,
  shiftDate,
  shiftStartTime,
  venueAddress,
  crewList = [],
  onApplyValues,
}) {
  const [selectedCrewId, setSelectedCrewId] = useState("");
  const [originAddress, setOriginAddress] = useState("");
  const [destinationAddress, setDestinationAddress] = useState("");
  const [isCalculating, setIsCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Filter crew list to only show ACTIVE staff with valid addresses
  const eligibleCrew = useMemo(() => {
    return crewList.filter(crew => 
      crew.status === "active" && hasValidAddress(crew)
    );
  }, [crewList]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setResult(null);
      setError(null);
      setSelectedCrewId("");
      setOriginAddress("");
      // Set destination from venue address prop
      setDestinationAddress(venueAddress || "");
    }
  }, [open, venueAddress]);

  // Update origin address when crew is selected
  useEffect(() => {
    if (selectedCrewId) {
      const crew = eligibleCrew.find(c => c.id === selectedCrewId);
      if (crew) {
        const address = getStaffDisplayAddress(crew);
        if (address) {
          setOriginAddress(address);
        }
      }
    }
  }, [selectedCrewId, eligibleCrew]);

  // Calculate arrival time (shift start - 30 minutes)
  const getArrivalDateTime = () => {
    if (!shiftDate || !shiftStartTime) return null;
    try {
      const dateTime = new Date(`${shiftDate}T${shiftStartTime}`);
      dateTime.setMinutes(dateTime.getMinutes() - 30);
      return dateTime.toISOString();
    } catch {
      return null;
    }
  };

  const handleCalculate = async () => {
    if (!originAddress || !destinationAddress) {
      setError("Both origin and destination addresses are required");
      return;
    }

    setIsCalculating(true);
    setError(null);
    setResult(null);

    try {
      const response = await getTravelAdvisory({
        origin_address: originAddress,
        destination_address: destinationAddress,
        arrival_datetime: getArrivalDateTime()
      });

      if (response.data.error_message) {
        setError(response.data.error_message);
      } else {
        setResult(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to calculate travel estimate");
    } finally {
      setIsCalculating(false);
    }
  };

  const handleApplyValues = () => {
    if (result && onApplyValues) {
      onApplyValues({
        distance_km: result.distance_km,
        duration_hours: result.duration_minutes / 60,
        toll_estimate: result.toll_estimate
      });
      onOpenChange(false);
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Car className="h-5 w-5 text-primary" />
            Travel Advisor
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Advisory Notice */}
          <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
            <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-500">
              This is an advisory tool. Values are suggestions only and will not be saved until you confirm.
            </p>
          </div>

          {/* Origin - Crew Address Selector */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              From Address (Crew Start Location)
            </Label>
            {eligibleCrew.length > 0 ? (
              <Select value={selectedCrewId} onValueChange={setSelectedCrewId}>
                <SelectTrigger data-testid="travel-advisor-crew-select">
                  <SelectValue placeholder="Select active crew with address..." />
                </SelectTrigger>
                <SelectContent>
                  {eligibleCrew.map((crew) => {
                    const cityDisplay = crew.address?.city || crew.address_city;
                    return (
                      <SelectItem key={crew.id} value={crew.id}>
                        {crew.first_name} {crew.last_name}
                        {cityDisplay ? ` (${cityDisplay})` : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-muted-foreground">
                No active crew with addresses available
              </p>
            )}
            <Input
              value={originAddress}
              onChange={(e) => setOriginAddress(e.target.value)}
              placeholder="Enter address manually or select crew above..."
              className="text-sm"
              data-testid="travel-advisor-origin-input"
            />
            <p className="text-xs text-muted-foreground">
              Selecting a crew member will auto-fill this field. You can still edit manually.
            </p>
          </div>

          {/* Destination - Editable with Venue default */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              Destination Address (Venue)
            </Label>
            <Input
              value={destinationAddress}
              onChange={(e) => setDestinationAddress(e.target.value)}
              placeholder="Enter destination address..."
              className="text-sm"
              data-testid="travel-advisor-destination-input"
            />
            <p className="text-xs text-muted-foreground">
              Pre-filled from venue location. Edit if needed.
            </p>
          </div>

          {/* Shift Info */}
          <div className="flex gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{shiftDate} at {shiftStartTime}</span>
            </div>
          </div>

          {/* Calculate Button */}
          <Button
            onClick={handleCalculate}
            disabled={isCalculating || !originAddress || !destinationAddress}
            className="w-full"
            data-testid="travel-advisor-calculate-btn"
          >
            {isCalculating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <Calculator className="h-4 w-4 mr-2" />
                Calculate Travel Estimate
              </>
            )}
          </Button>

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 rounded-lg border border-red-500/30">
              <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-500">{error}</p>
            </div>
          )}

          {/* Results */}
          {result && !error && (
            <div className="space-y-3 p-4 bg-surface-elevated rounded-lg border border-primary/30">
              <h4 className="text-sm font-medium text-primary flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Travel Estimate Results
              </h4>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-2 bg-background rounded">
                  <p className="text-2xl font-bold text-foreground">{result.distance_km}</p>
                  <p className="text-xs text-muted-foreground">km</p>
                </div>
                <div className="text-center p-2 bg-background rounded">
                  <p className="text-2xl font-bold text-foreground">{formatDuration(result.duration_minutes)}</p>
                  <p className="text-xs text-muted-foreground">travel time</p>
                </div>
                <div className="text-center p-2 bg-background rounded">
                  <p className="text-2xl font-bold text-foreground">€{result.toll_estimate.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">tolls</p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-2">
                These are advisory values. You must confirm to apply them.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="travel-advisor-cancel-btn"
          >
            Cancel
          </Button>
          {result && !error && (
            <Button
              onClick={handleApplyValues}
              data-testid="travel-advisor-apply-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Apply to Travel Tasks
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TravelAdvisorModal;
